import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn, ShieldCheck, Mail, Lock, User as UserIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { exchangeSession, setToken, registerUser, loginUser } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const AUTH_BASE = "https://auth.emergentagent.com";

export const startGoogleLogin = (returnTo = "/profile") => {
  const redirect = `${window.location.origin}/auth/callback?return=${encodeURIComponent(returnTo)}`;
  window.location.href = `${AUTH_BASE}/?redirect=${encodeURIComponent(redirect)}`;
};

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const LoginPage = () => {
  const { user, loading, setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to="/profile" replace />;

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = mode === "register"
        ? await registerUser({ name: form.name, email: form.email, password: form.password })
        : await loginUser({ email: form.email, password: form.password });
      if (data.session_token) setToken(data.session_token);
      if (data.user) setUser(data.user); else await refresh();
      toast.success(mode === "register" ? "Account created!" : "Signed in successfully");
      navigate("/profile", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="login-page" className="ol-container py-16 flex justify-center">
      <div className="w-full max-w-md bg-white border border-[#E2E8F0] rounded-2xl p-8 shadow-sm">
        <div className="w-14 h-14 rounded-xl bg-[#EAF5FD] flex items-center justify-center mx-auto">
          <ShieldCheck className="text-[#1088D4]" size={28} />
        </div>
        <h1 className="mt-5 text-2xl font-bold font-heading text-[#273B59] text-center">
          {mode === "register" ? "Create your account" : "Sign in to OneLoan"}
        </h1>
        <p className="mt-2 text-sm text-[#475569] text-center">Track your loan applications and manage your profile securely.</p>

        <form data-testid="auth-form" onSubmit={onSubmit} className="mt-6 space-y-4">
          {mode === "register" && (
            <div>
              <Label className="text-xs font-medium text-[#475569] mb-1.5 block">Full Name</Label>
              <div className="relative">
                <UserIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <Input data-testid="auth-name" required value={form.name} onChange={upd("name")} placeholder="Your full name" className="pl-9" />
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs font-medium text-[#475569] mb-1.5 block">Email</Label>
            <div className="relative">
              <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input data-testid="auth-email" required type="email" value={form.email} onChange={upd("email")} placeholder="you@example.com" className="pl-9" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-[#475569] mb-1.5 block">Password</Label>
            <div className="relative">
              <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input data-testid="auth-password" required type="password" value={form.password} onChange={upd("password")} placeholder={mode === "register" ? "At least 6 characters" : "Your password"} className="pl-9" />
            </div>
          </div>
          <Button data-testid="auth-submit-btn" type="submit" disabled={busy} className="w-full ol-btn-primary h-11 text-base">
            {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <LogIn className="mr-2" size={16} />}
            {mode === "register" ? "Create Account" : "Sign In"}
          </Button>
        </form>

        <p className="mt-4 text-center text-sm text-[#475569]">
          {mode === "register" ? "Already have an account?" : "New to OneLoan?"}{" "}
          <button data-testid="auth-toggle-btn" type="button" onClick={() => setMode(mode === "register" ? "login" : "register")} className="font-semibold text-[#1088D4] hover:underline">
            {mode === "register" ? "Sign in" : "Create an account"}
          </button>
        </p>

        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-[#E2E8F0]" />
          <span className="text-xs text-[#94A3B8]">or</span>
          <div className="h-px flex-1 bg-[#E2E8F0]" />
        </div>

        <Button data-testid="google-login-btn" variant="outline" onClick={() => startGoogleLogin()} className="w-full h-11 text-base border-[#E2E8F0] text-[#273B59] hover:bg-[#F7F9FC]">
          Continue with Google
        </Button>
        <p className="mt-4 text-[10px] text-[#94A3B8] text-center">By continuing you agree to our Terms & Privacy Policy.</p>
      </div>
    </div>
  );
};

export const AuthCallback = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(false);

  useEffect(() => {
    const run = async () => {
      const hash = window.location.hash || "";
      const params = new URLSearchParams(hash.replace(/^#/, ""));
      const sessionId = params.get("session_id");
      const returnTo = new URLSearchParams(window.location.search).get("return") || "/profile";
      if (!sessionId) { setError(true); return; }
      try {
        const data = await exchangeSession(sessionId);
        if (data.session_token) setToken(data.session_token);
        await refresh();
        toast.success("Signed in successfully");
        navigate(returnTo, { replace: true });
      } catch {
        setError(true);
        toast.error("Sign in failed. Please try again.");
      }
    };
    run();
  }, [navigate, refresh]);

  return (
    <div data-testid="auth-callback" className="ol-container py-24 flex flex-col items-center justify-center text-center">
      {error ? (
        <>
          <p className="text-[#273B59] font-semibold">We couldn't complete sign in.</p>
          <Button data-testid="retry-login-btn" onClick={() => navigate("/login")} className="mt-4 ol-btn-primary">Try again</Button>
        </>
      ) : (
        <>
          <Loader2 className="animate-spin text-[#1088D4]" size={36} />
          <p className="mt-4 text-sm text-[#475569]">Completing sign in…</p>
        </>
      )}
    </div>
  );
};

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="ol-container py-24 flex justify-center" data-testid="protected-loading">
        <Loader2 className="animate-spin text-[#1088D4]" size={32} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};
